import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Layers, Save, RefreshCw, Plus, Trash2, GripVertical, Eye, EyeOff,
  FileText, Mail, Phone, Calendar, Receipt, DollarSign, CalendarPlus,
  Settings, ChevronDown, ChevronUp, Edit2, Check, X, Bell, Activity,
  MessageSquare, CheckCircle2, FileEdit, ToggleLeft, ToggleRight
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const ICON_MAP = {
  FileText, Mail, Phone, Calendar, Receipt, DollarSign, CalendarPlus,
  Settings, Layers, Eye, Bell, Activity, MessageSquare, CheckCircle2, FileEdit
};

const COLOR_MAP = {
  blue: { active: "text-blue-400 border-blue-400 bg-blue-500/20", dot: "bg-blue-400" },
  indigo: { active: "text-indigo-400 border-indigo-400 bg-indigo-500/20", dot: "bg-indigo-400" },
  emerald: { active: "text-emerald-400 border-emerald-400 bg-emerald-500/20", dot: "bg-emerald-400" },
  cyan: { active: "text-cyan-400 border-cyan-400 bg-cyan-500/20", dot: "bg-cyan-400" },
  purple: { active: "text-purple-400 border-purple-400 bg-purple-500/20", dot: "bg-purple-400" },
  amber: { active: "text-amber-400 border-amber-400 bg-amber-500/20", dot: "bg-amber-400" },
  green: { active: "text-green-400 border-green-400 bg-green-500/20", dot: "bg-green-400" },
  orange: { active: "text-orange-400 border-orange-400 bg-orange-500/20", dot: "bg-orange-400" },
  pink: { active: "text-pink-400 border-pink-400 bg-pink-500/20", dot: "bg-pink-400" },
};

const AVAILABLE_COLORS = Object.keys(COLOR_MAP);

function SortableTab({ tab, onToggle, onEdit, onDelete }) {
  const {
    attributes, listeners, setNodeRef, transform, transition,
  } = useSortable({ id: tab.id });

  const style = { transform: CSS.Transform.toString(transform), transition };
  const IconComponent = ICON_MAP[tab.icon] || FileText;
  const colorStyle = COLOR_MAP[tab.color] || COLOR_MAP.blue;
  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(tab.label);

  const handleSaveEdit = () => {
    onEdit(tab.id, { label: editLabel });
    setIsEditing(false);
  };

  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
        tab.enabled ? 'bg-slate-800/50 border-slate-600' : 'bg-slate-900/50 border-slate-700 opacity-50'
      }`}
    >
      <div {...attributes} {...listeners} className="cursor-grab text-slate-500 hover:text-slate-300">
        <GripVertical size={18} />
      </div>

      <div className={`w-2 h-8 rounded-full ${tab.enabled ? colorStyle.dot : 'bg-slate-600'}`} />

      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
        tab.enabled ? colorStyle.active : 'bg-slate-700 text-slate-500'
      }`}>
        <IconComponent size={18} />
      </div>

      <div className="flex-1">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input type="text" value={editLabel} onChange={(e) => setEditLabel(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-sm text-white flex-1" autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
            />
            <button onClick={handleSaveEdit} className="p-1 bg-green-500 rounded text-white"><Check size={14} /></button>
            <button onClick={() => setIsEditing(false)} className="p-1 bg-slate-600 rounded text-white"><X size={14} /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className={`font-medium ${tab.enabled ? 'text-white' : 'text-slate-500'}`}>{tab.label}</span>
            {tab.badge_field && (
              <span className="text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded">badge</span>
            )}
          </div>
        )}
        <span className="text-xs text-slate-500">{tab.id}</span>
      </div>

      <div className="flex items-center gap-1.5">
        <button onClick={() => setIsEditing(true)} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors" title="Modifier">
          <Edit2 size={14} />
        </button>
        <button onClick={() => onToggle(tab.id)}
          className={`p-1.5 rounded transition-colors ${
            tab.enabled ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-slate-700 text-slate-500 hover:bg-slate-600'
          }`} title={tab.enabled ? "Desactiver" : "Activer"}
        >
          {tab.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        <button onClick={() => onDelete(tab.id)} className="p-1.5 hover:bg-red-500/20 rounded text-slate-400 hover:text-red-400 transition-colors" title="Supprimer">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function AddTabModal({ isOpen, onClose, onSave, existingIds }) {
  const [label, setLabel] = useState("");
  const [id, setId] = useState("");
  const [icon, setIcon] = useState("FileText");
  const [color, setColor] = useState("blue");

  const handleSave = () => {
    if (!label.trim()) { toast.error("Label requis"); return; }
    const newId = id.trim() || label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    if (existingIds.includes(newId)) { toast.error("Cet ID existe deja"); return; }
    onSave({ id: newId, label: label.trim(), icon, color, badge_field: null, enabled: true, order: 99 });
    setLabel(""); setId(""); setIcon("FileText"); setColor("blue");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl w-full max-w-md border border-slate-700">
        <div className="p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Plus size={20} className="text-purple-400" /> Nouvel Onglet
          </h3>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Label *</label>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex: Documents"
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">ID (auto si vide)</label>
            <input type="text" value={id} onChange={(e) => setId(e.target.value)} placeholder="Ex: documents"
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Icone</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(ICON_MAP).map(([iconId, IconComp]) => (
                <button key={iconId} onClick={() => setIcon(iconId)}
                  className={`p-2 rounded-lg transition-colors ${icon === iconId ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}
                  title={iconId}>
                  <IconComp size={18} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Couleur</label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_COLORS.map((c) => (
                <button key={c} onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${COLOR_MAP[c].dot} ${color === c ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  title={c} />
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">Annuler</button>
          <button onClick={handleSave} className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors flex items-center gap-2">
            <Plus size={16} /> Creer
          </button>
        </div>
      </div>
    </div>
  );
}

function FeatureFlagToggle({ label, description, value, onChange }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
      <div>
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="text-xs text-slate-400">{description}</p>
      </div>
      <button onClick={() => onChange(!value)}
        className={`p-1 rounded-lg transition-colors ${value ? 'text-emerald-400' : 'text-slate-500'}`}
        data-testid={`flag-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {value ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
      </button>
    </div>
  );
}

export default function DynamicTabsConfig() {
  const [tabs, setTabs] = useState([]);
  const [featureFlags, setFeatureFlags] = useState({ header_personnalise: false, modal_editor_actif: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true);
      try {
        const resp = await authFetch(`${API}/config/dynamic-tabs`);
        const data = await resp.json();
        setTabs(data.tabs || []);
        setFeatureFlags(data.feature_flags || { header_personnalise: false, modal_editor_actif: false });
      } catch (e) {
        console.error("Erreur chargement config:", e);
        toast.error("Erreur lors du chargement");
      }
      setLoading(false);
    };
    loadConfig();
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const resp = await authFetch(`${API}/config/dynamic-tabs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tabs, feature_flags: featureFlags })
      });
      if (resp.ok) {
        toast.success("Configuration sauvegardee !");
        setHasChanges(false);
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (e) {
      console.error("Erreur sauvegarde:", e);
      toast.error("Erreur lors de la sauvegarde");
    }
    setSaving(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setTabs((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex).map((item, idx) => ({ ...item, order: idx + 1 }));
      });
      setHasChanges(true);
    }
  };

  const handleToggle = (tabId) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, enabled: !t.enabled } : t));
    setHasChanges(true);
  };

  const handleEdit = (tabId, updates) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, ...updates } : t));
    setHasChanges(true);
  };

  const handleDelete = (tabId) => {
    if (window.confirm("Supprimer cet onglet ?")) {
      setTabs(prev => prev.filter(t => t.id !== tabId));
      setHasChanges(true);
    }
  };

  const handleAddTab = (newTab) => {
    setTabs(prev => [...prev, { ...newTab, order: prev.length + 1 }]);
    setHasChanges(true);
  };

  const updateFlag = (key, value) => {
    setFeatureFlags(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const enabledCount = tabs.filter(t => t.enabled).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
            <Layers className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Selections Dynamiques</h3>
            <p className="text-sm text-slate-400">{enabledCount} onglet(s) actif(s) sur {tabs.length}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
            <Plus size={16} /> Ajouter
          </button>
          <button onClick={saveConfig} disabled={saving || !hasChanges}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              hasChanges ? 'bg-purple-500 hover:bg-purple-600 text-white' : 'bg-slate-700 text-slate-400 cursor-not-allowed'
            }`}>
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Feature Flags */}
      <div className="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4 space-y-3">
        <h4 className="text-sm font-medium text-slate-400 flex items-center gap-2">
          <Settings size={14} /> Fonctionnalites avancees
        </h4>
        <FeatureFlagToggle
          label="Header personnalise"
          description="Utiliser la configuration custom du header de la fiche client"
          value={featureFlags.header_personnalise}
          onChange={(v) => updateFlag('header_personnalise', v)}
        />
        <FeatureFlagToggle
          label="Editeur de modal"
          description="Activer l'editeur WYSIWYG des sections de la fiche client"
          value={featureFlags.modal_editor_actif}
          onChange={(v) => updateFlag('modal_editor_actif', v)}
        />
      </div>

      {/* Onglets */}
      <div className="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tabs.map(t => t.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {tabs.map((tab) => (
                <SortableTab key={tab.id} tab={tab} onToggle={handleToggle} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        {tabs.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucun onglet configure</p>
            <button onClick={() => setShowAddModal(true)} className="mt-2 text-purple-400 hover:text-purple-300">Ajouter un onglet</button>
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4">
        <h4 className="text-sm font-medium text-slate-400 mb-3">Apercu des onglets actifs</h4>
        <div className="flex flex-wrap gap-2 border-b border-slate-700 pb-3">
          {tabs.filter(t => t.enabled).sort((a, b) => a.order - b.order).map(tab => {
            const IconComponent = ICON_MAP[tab.icon] || FileText;
            const colorStyle = COLOR_MAP[tab.color] || COLOR_MAP.blue;
            return (
              <div key={tab.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${colorStyle.active}`}>
                <IconComponent size={16} />
                <span className="text-sm">{tab.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <AddTabModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSave={handleAddTab} existingIds={tabs.map(t => t.id)} />
    </div>
  );
}
